<template>
    <div
        v-if="isOpen"
        class="sidebar-overlay fixed inset-0 z-[999] bg-black/10"
        @click="closeSidebar"
    ></div>

    <Transition name="sidebar-slide">
        <div
            v-show="isOpen"
            class="flex-none base-sidebar pointer-events-auto max-w-md bottom-3 transform fixed right-3 overflow-hidden z-[9999] min-w-full lg:min-w-[28rem] ring-1 ring-white/5 rounded-lg border border-slate-200 dark:border-slate-700"
            :class="[
                positionClass,
                customClasses,
                fullScreenOnMobile
                    ? 'max-sm:!inset-0 max-sm:!max-w-none max-sm:!rounded-none max-sm:!border-0'
                    : '',
            ]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="title ? 'sidebar-title' : null"
        >
            <div
                ref="scrollableContainer"
                class="flex h-full flex-col overflow-y-auto relative"
                :class="bodyClass"
            >
                <div
                    v-if="showHeader"
                    class="flex justify-between items-start self-stretch w-full px-8 pt-6 sticky top-0 z-[25]"
                    :class="headerClass"
                >
                    <div class="flex items-center flex-1">
                        <slot name="header-left">
                            <div
                                v-if="title"
                                id="sidebar-title"
                                class="text-lg font-medium text-slate-700 dark:text-slate-300"
                            >
                                {{ title }}
                            </div>
                        </slot>
                    </div>

                    <div class="flex items-center ml-2">
                        <slot name="header-right">
                            <button
                                type="button"
                                class="p-2 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 active:bg-slate-200 dark:active:bg-slate-600 transition-colors duration-150"
                                @click="closeSidebar"
                            >
                                <span class="sr-only">Cerrar panel</span>
                                <Icon
                                    icon="close"
                                    :width="24"
                                    :height="24"
                                    class="h-6 w-6"
                                />
                            </button>
                        </slot>
                    </div>
                </div>

                <div class="flex-1">
                    <slot>
                        <!-- Default content when no slot content is provided -->
                        <div class="text-center text-slate-500 dark:text-slate-400 py-8">
                            Sidebar content
                        </div>
                    </slot>
                </div>

                <div v-if="$slots.footer" class="px-4 sm:px-6 lg:px-8 py-4 border-t border-slate-200 dark:border-slate-700">
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import Icon from '@/components/ui/icon/icon.vue';

export default {
    name: 'BaseSidebar',
    components: {
        Icon,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        customClasses: {
            type: [String, Array, Object],
            default: '',
        },
        onClose: {
            type: Function,
            default: null,
        },
        hasInvitationBanner: {
            type: Boolean,
            default: false,
        },
        // Background for the scrollable panel body (override to add a gradient).
        bodyClass: {
            type: [String, Array, Object],
            default: 'bg-white dark:bg-slate-800',
        },
        // Background for the sticky header (override to let a body gradient show through).
        headerClass: {
            type: [String, Array, Object],
            default: 'bg-white dark:bg-slate-800',
        },
        // Render edge-to-edge full screen below the `sm` breakpoint (mobile),
        // reverting to the docked right panel from `sm` up. Opt-in so the
        // other consumers keep their current behavior.
        fullScreenOnMobile: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close'],
    computed: {
        positionClass() {
            return this.hasInvitationBanner ? 'top-16' : 'top-0';
        },
    },
    methods: {
        closeSidebar() {
            if (this.onClose && typeof this.onClose === 'function') {
                this.onClose();
            } else {
                this.$emit('close');
            }
        },

        scrollToTop() {
            this.$nextTick(() => {
                if (this.$refs.scrollableContainer) {
                    this.$refs.scrollableContainer.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.sidebar-overlay {
    /* background handled by Tailwind classes: bg-black/10 */
    position: fixed;
    inset: 0;
    z-index: 140;
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
    transition:
        transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.sidebar-slide-enter-to,
.sidebar-slide-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.base-sidebar {
    will-change: transform, opacity;
}
</style>

